import { everyTrue, someTrue } from 'aa_common/front-end/helpers'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import { useEffect, useState } from 'react'

import { checkIsEventAfterAdjusted } from '../../helper/check-event-after-adjusted'
import { useTermYearMonthByDate } from '../../hooks/use-term-year-month-by-date'
import { getCorrectionBlockedReason } from '../helper'

type Params = {
  biid: string
  isCorrectionMode: boolean
  isCorrectionBlockedByEventHistory: boolean
  adjusted: boolean
  isCorrectionBlockedByMonthlyClosing: boolean
  transitionAt?: string | null
  isCorrectionBlockedByAcquisitionMonthlyClosing: boolean
  acquiredAt?: string | null
  isBlockedByRetroactiveRegistration?: boolean
  recognizedAt?: string | null
  isDraftFA?: boolean
}

export const useCorrectionBlocked = ({
  biid,
  isCorrectionMode,
  isCorrectionBlockedByEventHistory,
  adjusted,
  isCorrectionBlockedByMonthlyClosing,
  transitionAt,
  isCorrectionBlockedByAcquisitionMonthlyClosing,
  acquiredAt,
  isBlockedByRetroactiveRegistration,
  recognizedAt,
  isDraftFA,
}: Params) => {
  const { isEnable: isCorrectionBlockedReasonEnabled } = useCheckFlag(FeatureFlagNames.AssetCorrectionBlockedReason)

  // Draft fixed assets can be edited with formMode=CORRECTION too, but the correction-blocked reason/field-lock
  // only applies when correcting a normal (non-draft) fixed asset.
  const isCorrectionBlocked = everyTrue(
    !!isCorrectionBlockedReasonEnabled,
    isCorrectionMode,
    !isDraftFA,
    someTrue(
      isCorrectionBlockedByEventHistory,
      adjusted,
      isCorrectionBlockedByMonthlyClosing,
      isCorrectionBlockedByAcquisitionMonthlyClosing,
      !!isBlockedByRetroactiveRegistration
    )
  )

  // undefined until the adjust-history check settles, so the reason box never shows a message that is about to flip.
  const [isEventRegisteredAfterAdjusted, setIsEventRegisteredAfterAdjusted] = useState<boolean>()
  const shouldCompareAdjustedWithEvents = everyTrue(isCorrectionBlocked, adjusted, isCorrectionBlockedByEventHistory)
  const isCheckingEventAfterAdjusted = shouldCompareAdjustedWithEvents && isEventRegisteredAfterAdjusted === undefined

  useEffect(() => {
    if (!shouldCompareAdjustedWithEvents) return
    checkIsEventAfterAdjusted(biid).then(setIsEventRegisteredAfterAdjusted)
  }, [biid, shouldCompareAdjustedWithEvents])

  // Accounting term year/month of the date shown in the monthly-closing message; only fetched when relevant.
  // A transition date takes over: the acquisition date is ignored when it exists. A closed registration month of a
  // retroactive FA takes over the acquisition date too.
  const { termYearMonth: transitionTermYearMonth, isLoading: isLoadingTransitionTerm } = useTermYearMonthByDate(
    isCorrectionBlockedByMonthlyClosing ? transitionAt : undefined
  )
  const { termYearMonth: acquiredTermYearMonth, isLoading: isLoadingAcquiredTerm } = useTermYearMonthByDate(
    everyTrue(!transitionAt, !isBlockedByRetroactiveRegistration, isCorrectionBlockedByAcquisitionMonthlyClosing)
      ? acquiredAt
      : undefined
  )
  const { termYearMonth: recognizedTermYearMonth, isLoading: isLoadingRecognizedTerm } = useTermYearMonthByDate(
    !transitionAt && isBlockedByRetroactiveRegistration ? recognizedAt : undefined
  )

  // Fields lock right away, but the reason waits until the adjust-history check and term lookups settle.
  const correctionBlockedReason = everyTrue(
    isCorrectionBlocked,
    !isCheckingEventAfterAdjusted,
    !isLoadingTransitionTerm,
    !isLoadingAcquiredTerm,
    !isLoadingRecognizedTerm
  )
    ? getCorrectionBlockedReason({
        biid,
        isCorrectionBlockedByEventHistory,
        adjusted,
        transitionAt,
        isCorrectionBlockedByMonthlyClosing,
        isCorrectionBlockedByAcquisitionMonthlyClosing,
        isBlockedByRetroactiveRegistration: !!isBlockedByRetroactiveRegistration,
        acquiredAt,
        recognizedAt,
        isEventRegisteredAfterAdjusted,
        transitionTermYearMonth,
        acquiredTermYearMonth,
        recognizedTermYearMonth,
      })
    : null

  return {
    correctionBlockedReason,
    isCorrectionBlocked,
    isCorrectionBlockedReasonEnabled: !!isCorrectionBlockedReasonEnabled,
  }
}
